import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api, { cacheGet, cacheSet } from '../api';

export default function Settings() {
  const [me, setMe] = useState(() => cacheGet('me', {}));
  const [settings, setSettings] = useState(() => cacheGet('settings', {}));
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');

  async function load() {
    const [meRes, settingsRes] = await Promise.all([
      api.get('/me').catch(() => ({ data: cacheGet('me', {}) })),
      api.get('/settings').catch(() => ({ data: cacheGet('settings', {}) })),
    ]);
    const meNext = meRes.data && typeof meRes.data === 'object' ? meRes.data : {};
    const setNextObj = settingsRes.data && typeof settingsRes.data === 'object' ? settingsRes.data : {};
    setMe(meNext);
    setSettings(setNextObj);
    cacheSet('me', meNext);
    cacheSet('settings', setNextObj);
  }

  useEffect(() => {
    load().catch(() => toast.error('Ошибка загрузки'));
  }, []);

  const archivePassword = me.archive_password || settings.archive_password || '—';
  const daysLeft = me.days_left ?? settings.subscription_days ?? '—';
  const [archiveEdit, setArchiveEdit] = useState('');
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiUrl, setAiUrl] = useState('');
  const [aiKey, setAiKey] = useState('');
  const [aiModel, setAiModel] = useState('dall-e-3');
  const [aiPrompt, setAiPrompt] = useState('');

  useEffect(() => {
    setAiEnabled(Boolean(settings.ai_icon_enabled));
    setAiUrl(settings.ai_icon_api_url || '');
    setAiKey(settings.ai_icon_api_key || '');
    setAiModel(settings.ai_icon_model || 'dall-e-3');
    setAiPrompt(
      settings.ai_icon_prompt ||
        'App icon for Windows desktop program named "{name}". Clean flat modern software logo, simple shapes, high contrast, centered, no text, no watermark, square, professional utility style.'
    );
  }, [settings]);

  async function saveArchive(e) {
    e.preventDefault();
    const v = archiveEdit.trim();
    if (v.length < 4) {
      toast.error('Минимум 4 символа');
      return;
    }
    try {
      await api.put('/settings', { archive_password: v });
      toast.success('Пароль архива обновлён');
      setArchiveEdit('');
      load();
    } catch {
      toast.error('Не удалось сохранить');
    }
  }

  async function saveAiIcon(e) {
    e.preventDefault();
    try {
      await api.put('/settings', {
        ai_icon_enabled: aiEnabled,
        ai_icon_api_url: aiUrl.trim(),
        ai_icon_api_key: aiKey,
        ai_icon_model: aiModel.trim() || 'dall-e-3',
        ai_icon_prompt: aiPrompt.trim(),
      });
      toast.success('AI-иконки сохранены');
      load();
    } catch {
      toast.error('Не удалось сохранить AI');
    }
  }

  async function changePassword(e) {
    e.preventDefault();
    if (next !== confirm) {
      toast.error('Пароли не совпадают');
      return;
    }
    if (next.length < 12) {
      toast.error('Минимум 12 символов');
      return;
    }
    try {
      await api.post('/auth/password', { current, next });
      toast.success('Пароль изменён');
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch {
      toast.error('Не удалось сменить пароль');
    }
  }

  return (
    <div>
      <div className="rm-page-title">Настройки</div>

      <div className="rm-kpi-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', maxWidth: 720 }}>
        <div className="rm-card rm-kpi">
          <div className="label">ПАРОЛЬ АРХИВА</div>
          <div className="value" style={{ fontSize: 22 }}>{archivePassword}</div>
        </div>
        <div className="rm-card rm-kpi">
          <div className="label">ОСТАЛОСЬ ДНЕЙ</div>
          <div className="value" style={{ fontSize: 22 }}>{daysLeft}</div>
        </div>
      </div>

      <div className="rm-card" style={{ maxWidth: 480, marginTop: 8 }}>
        <form className="rm-form" onSubmit={saveArchive}>
          <h3 style={{ marginBottom: 4 }}>Пароль итогового архива</h3>
          <p style={{ color: '#64748B', fontSize: 13, marginBottom: 8 }}>
            Только для кнопки «Выгрузить новые». Одиночные логи — без пароля.
            Сейчас: <b>{archivePassword}</b>
          </p>
          <div className="rm-field">
            <label>Новый пароль архива</label>
            <input
              type="text"
              value={archiveEdit}
              onChange={(e) => setArchiveEdit(e.target.value)}
              placeholder="новый пароль"
              autoComplete="off"
            />
          </div>
          <button type="submit" className="rm-btn primary" style={{ justifyContent: 'center', padding: 12 }}>
            Сохранить пароль архива
          </button>
        </form>
      </div>

      <div className="rm-card" style={{ maxWidth: 640, marginTop: 8 }}>
        <form className="rm-form" onSubmit={saveAiIcon}>
          <h3 style={{ marginBottom: 4 }}>AI-иконки билдов</h3>
          <p style={{ color: '#64748B', fontSize: 13, marginBottom: 8 }}>
            Если PNG не загружен в Builder — панель дергает custom Images API (OpenAI-compatible
            <code style={{ margin: '0 4px' }}>/images/generations</code>
            ) и ставит результат как иконку EXE. Без API — геометрический random icon.
            В промпте доступны <code>{'{name}'}</code> и <code>{'{file}'}</code>.
          </p>
          <label className="rm-mod-chip" style={{ marginBottom: 10, display: 'inline-flex' }}>
            <input type="checkbox" checked={aiEnabled} onChange={(e) => setAiEnabled(e.target.checked)} />
            Включить AI-иконки
          </label>
          <div className="rm-field">
            <label>API URL</label>
            <input
              value={aiUrl}
              onChange={(e) => setAiUrl(e.target.value)}
              placeholder="https://api.openai.com/v1  или полный .../images/generations"
              autoComplete="off"
            />
          </div>
          <div className="rm-field">
            <label>API Key</label>
            <input
              type="password"
              value={aiKey}
              onChange={(e) => setAiKey(e.target.value)}
              placeholder="sk-..."
              autoComplete="off"
            />
          </div>
          <div className="rm-field">
            <label>Model</label>
            <input value={aiModel} onChange={(e) => setAiModel(e.target.value)} placeholder="dall-e-3" />
          </div>
          <div className="rm-field">
            <label>Промпт</label>
            <textarea
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              rows={4}
              style={{ width: '100%', fontFamily: 'inherit' }}
            />
          </div>
          <button type="submit" className="rm-btn primary" style={{ justifyContent: 'center', padding: 12 }}>
            Сохранить AI
          </button>
        </form>
      </div>

      <div className="rm-card" style={{ maxWidth: 480, marginTop: 8 }}>
        <form className="rm-form" onSubmit={changePassword}>
          <h3 style={{ marginBottom: 4 }}>Смена пароля панели</h3>
          <p style={{ color: '#64748B', fontSize: 13, marginBottom: 8 }}>
            Пользователь: <b>{me.username || '—'}</b> · План: <b>{me.plan || '—'}</b>
          </p>
          <div className="rm-field">
            <label>Текущий пароль</label>
            <input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>
          <div className="rm-field">
            <label>Новый пароль</label>
            <input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              autoComplete="new-password"
            />
          </div>
          <div className="rm-field">
            <label>Повтор нового пароля</label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              autoComplete="new-password"
            />
          </div>
          <button type="submit" className="rm-btn primary" style={{ justifyContent: 'center', padding: 12 }}>
            Сохранить пароль
          </button>
        </form>
      </div>
    </div>
  );
}
